import React, { useState } from "react";
import Header from "../components/Header.js";
import Footer from "../components/Footer.js";
import { getAppCheckToken } from "../utils/appCheck.js";
// import { validateContactForm } from "../utils/contactValidation.js";

function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });

  // Add the missing isSubmitting state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [modal, setModal] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log("Sending feedback data:", formData);

      // Get AppCheck token
      const appCheckToken = await getAppCheckToken();
      console.log("AppCheck token obtained");

      // Send feedback to backend
      const response = await fetch(
        "https://asia-south2-barma-sorig.cloudfunctions.net/sendFeedbackEmail",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Firebase-AppCheck": appCheckToken,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            mobile: formData.mobile,
            message: formData.message,
          }),
        }
      );

      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);

      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Response not ok:", errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      // Try to parse JSON
      let result;
      try {
        const responseText = await response.text();
        console.log("Raw response text:", responseText);
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        throw new Error("Invalid response from server");
      }

      console.log("Parsed result:", result);

      if (result.success) {
        setFormData({ name: "", email: "", mobile: "", message: "" });
        setModal({
          show: true,
          message: "Thank you for your feedback! We'll get back to you soon.",
          type: "success",
        });
        setTimeout(
          () => setModal({ show: false, message: "", type: "success" }),
          3000
        );
      } else {
        throw new Error(result.error || "Unknown error");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setModal({
        show: true,
        message: `There was an error submitting your feedback: ${error.message}`,
        type: "error",
      });
      setTimeout(
        () => setModal({ show: false, message: "", type: "error" }),
        4000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div className="bg-gradient-to-br from-gray-50 via-teal-50/30 to-white min-h-screen">
        {/* Modal */}
        {modal.show && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <div
              className={`pointer-events-auto px-8 py-5 rounded-xl shadow-lg text-lg font-semibold transition-all duration-300
                ${
                  modal.type === "success"
                    ? "bg-white text-teal-700 border border-teal-300"
                    : "bg-white text-red-600 border border-red-300"
                }
              `}
              style={{
                position: "absolute",
                top: "20%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                minWidth: "300px",
                textAlign: "center",
              }}
            >
              {modal.message}
            </div>
          </div>
        )}

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800 text-white py-12 overflow-hidden">
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-teal-100 to-cyan-200 drop-shadow-2xl animate-fade-in">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-teal-100 font-light animate-fade-in delay-300">
              We'd love to hear from you! Reach out to us with your questions,
              feedback, or inquiries.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="relative py-24 overflow-visible">
          {/* Decorative gradients */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-teal-200/20 to-cyan-300/10 rounded-full blur-2xl translate-x-1/3 -translate-y-1/3 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-emerald-200/20 to-teal-300/10 rounded-full blur-2xl -translate-x-1/3 translate-y-1/3 pointer-events-none"></div>

          <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-24 items-start relative z-10">
            {/* Feedback Form */}
            <div className="flex flex-col justify-center">
              <div className="mb-10 text-left">
                <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-teal-800 drop-shadow-lg mb-2">
                  Send Us a Message
                </h2>
                <p className="text-lg text-gray-600">
                  Fill out the form and our team will get back to you soon.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-gray-700 font-semibold mb-2"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full border-0 border-b-2 border-teal-400 bg-transparent px-0 py-3 text-lg focus:border-teal-600 focus:outline-none placeholder-gray-400 transition-all duration-300"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-gray-700 font-semibold mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full border-0 border-b-2 border-teal-400 bg-transparent px-0 py-3 text-lg focus:border-teal-600 focus:outline-none placeholder-gray-400 transition-all duration-300"
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="mobile"
                    className="block text-gray-700 font-semibold mb-2"
                  >
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="w-full border-0 border-b-2 border-teal-400 bg-transparent px-0 py-3 text-lg focus:border-teal-600 focus:outline-none placeholder-gray-400 transition-all duration-300"
                    placeholder="Enter your mobile number"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-gray-700 font-semibold mb-2"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full border-0 border-b-2 border-teal-400 bg-transparent px-0 py-3 text-lg focus:border-teal-600 focus:outline-none placeholder-gray-400 transition-all duration-300 resize-none"
                    placeholder="Enter your message"
                    rows="5"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-700 text-white px-8 py-4 rounded-xl hover:from-teal-700 hover:via-teal-800 hover:to-cyan-800 transition-all duration-300 font-bold text-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div className="flex flex-col gap-10">
              <div>
                <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-teal-800 drop-shadow-lg mb-4">
                  Visit Us
                </h2>
                <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-teal-200">
                  <iframe
                    title="Google Map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3539.660813101938!2d89.63403127531538!3d27.479817176313816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39e1938f16a623b3%3A0xdc9f6897422512d3!2sBarma%20Sorig%20Healing%20Center!5e0!3m2!1sen!2sau!4v1747217117839!5m2!1sen!2sau"
                    width="100%"
                    height="320"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    className="rounded-3xl"
                  ></iframe>
                </div>
              </div>
              <div className="bg-transparent p-0">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-teal-100 to-teal-200 text-teal-600 text-xl shadow-lg">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div>
                      <p className="text-gray-700 font-bold text-lg mb-1">
                        Address:
                      </p>
                      <p className="text-gray-600 text-lg">
                        Chubachu, Thimphu 11001, Bhutan
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200 text-blue-600 text-xl shadow-lg">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div>
                      <p className="text-gray-700 font-bold text-lg mb-1">
                        Phone:
                      </p>
                      <p className="text-gray-600 text-lg">02-341361</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-green-100 to-green-200 text-green-600 text-xl shadow-lg">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div>
                      <p className="text-gray-700 font-bold text-lg mb-1">
                        Email:
                      </p>
                      <a
                        href="mailto:barmasorig@gmail.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-800 text-lg font-medium transition-colors duration-300 hover:underline"
                      >
                        barmasorig@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-purple-200 text-purple-600 text-xl shadow-lg">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div>
                      <p className="text-gray-700 font-bold text-lg mb-1">
                        Hours:
                      </p>
                      <p className="text-gray-600 text-lg">
                        Open All Week, 10AM - 8PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />

      {/* Custom styles */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        .delay-300 {
          animation-delay: 0.3s;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-pulse,
          .animate-ping,
          .animate-fade-in {
            animation: none;
          }
        }
      `}</style>
    </>
  );
}

export default ContactPage;
